/**
 * Robot-Assisted Battery Cabinet Assembly — concept-stage section for /neunexus.
 *
 * STRICT COMPLIANCE:
 *   - No NEURA logo, no "NeuNexus × NEURA" wording, no partnership/customer claim
 *   - No private meetings, names, emails or dates
 *   - Presented as concept-stage future application, not operational
 *   - CTA scrolls to the existing #contact anchor — no new form created
 *
 * Additive only. Does NOT modify or remove any existing section.
 * Fully DE / EN / PT via LangCtx.
 */
import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";
import ConceptPresentationViewer from "@/pages/bsgotech/ConceptPresentationViewer";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  border:  "rgba(255,255,255,0.08)",
  surface: "rgba(255,255,255,0.03)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  cyan:    "#06B6D4",
  green:   "#22C55E",
  amber:   "#F59E0B",
};

const D = {
  en: {
    status: "Concept Stage · Future Industrial Application",
    title: "Robot-Assisted Battery Cabinet Assembly",
    intro: "NeuNexus is exploring a future robotic application to support battery cabinet assembly and visual inspection. The proposed first step is one simple, safe and repeatable component-handling task, supervised and approved by qualified technicians.",
    imgLabel: "Concept Simulation",
    imgAlt: "Concept simulation of a future NeuNexus workflow: a generic collaborative robotic arm next to an open battery cabinet with organized component trays and an inspection camera, while a qualified technician supervises the process near an emergency stop.",
    steps: [
      { n: "01", title: "Prepare the components", d: "Known parts are organized before assembly." },
      { n: "02", title: "Pick and move",           d: "The robot handles one approved component." },
      { n: "03", title: "Place",                   d: "The component is positioned inside the battery cabinet." },
      { n: "04", title: "Check",                   d: "The system checks the position and serial number." },
      { n: "05", title: "Human approval",          d: "A qualified technician reviews and approves the result." },
    ],
    humanTitle: "Human control remains essential",
    humanBody:  "The robot supports repetitive physical work. Electrical connections, error decisions, safety intervention and final product approval remain human responsibilities.",
    statusTitle: "Project Status",
    statuses: [
      { k: "Current",           v: "Founder-led concept with one proposed use case." },
      { k: "Under evaluation",  v: "Robot configuration, safety requirements, training process and pilot feasibility." },
      { k: "Future",            v: "Pilot cell, implementation team, software dashboard and industrial deployment." },
    ],
    disclaimer: "Concept simulation only. The robot configuration, safety requirements and implementation pathway have not yet been finalized. No confirmed technology partnership, technical approval or commercial deployment is implied.",
    cta: "Discuss a Future Pilot",
  },
  de: {
    status: "Konzeptphase · Zukünftige industrielle Anwendung",
    title: "Roboterunterstützte Batterie­schrank-Montage",
    intro: "NeuNexus untersucht eine zukünftige robotische Anwendung zur Unterstützung der Batterieschrank-Montage und der visuellen Inspektion. Der vorgeschlagene erste Schritt ist eine einfache, sichere und wiederholbare Komponenten-Handhabungsaufgabe, überwacht und freigegeben durch qualifizierte Techniker.",
    imgLabel: "Konzept-Simulation",
    imgAlt: "Konzeptsimulation eines zukünftigen NeuNexus-Workflows: ein generischer kollaborativer Roboterarm neben einem offenen Batterieschrank mit organisierten Bauteil-Trays und Inspektionskamera, während ein qualifizierter Techniker den Prozess in der Nähe eines Not-Halt-Tasters überwacht.",
    steps: [
      { n: "01", title: "Komponenten vorbereiten", d: "Bekannte Teile werden vor der Montage organisiert." },
      { n: "02", title: "Greifen und Bewegen",     d: "Der Roboter handhabt eine freigegebene Komponente." },
      { n: "03", title: "Platzieren",              d: "Die Komponente wird im Batterieschrank positioniert." },
      { n: "04", title: "Prüfen",                  d: "Das System prüft Position und Seriennummer." },
      { n: "05", title: "Menschliche Freigabe",    d: "Ein qualifizierter Techniker prüft und gibt das Ergebnis frei." },
    ],
    humanTitle: "Menschliche Kontrolle bleibt entscheidend",
    humanBody:  "Der Roboter unterstützt repetitive körperliche Arbeit. Elektrische Verbindungen, Fehlerentscheidungen, Sicherheitsintervention und finale Produktfreigabe bleiben menschliche Verantwortung.",
    statusTitle: "Projektstatus",
    statuses: [
      { k: "Aktuell",         v: "Vom Gründer geleitetes Konzept mit einem vorgeschlagenen Use Case." },
      { k: "In Bewertung",    v: "Roboterkonfiguration, Sicherheitsanforderungen, Schulungsprozess und Pilot-Machbarkeit." },
      { k: "Zukunft",         v: "Pilotzelle, Implementierungsteam, Software-Dashboard und industrielle Bereitstellung." },
    ],
    disclaimer: "Nur Konzept-Simulation. Roboterkonfiguration, Sicherheitsanforderungen und Implementierungsweg sind noch nicht finalisiert. Keine bestätigte Technologiepartnerschaft, technische Freigabe oder kommerzielle Bereitstellung ist impliziert.",
    cta: "Zukünftigen Piloten besprechen",
  },
  pt: {
    status: "Fase de Conceito · Aplicação Industrial Futura",
    title: "Montagem de Armários de Bateria Assistida por Robot",
    intro: "A NeuNexus está a explorar uma futura aplicação robótica para apoiar a montagem e inspecção visual de armários de bateria. O primeiro passo proposto é uma tarefa simples, segura e repetível de manipulação de componentes, supervisionada e aprovada por técnicos qualificados.",
    imgLabel: "Simulação de Conceito",
    imgAlt: "Simulação de conceito de um futuro fluxo NeuNexus: um braço robótico colaborativo genérico junto a um armário de bateria aberto com tabuleiros de componentes organizados e câmara de inspecção, enquanto um técnico qualificado supervisiona o processo perto de um botão de paragem de emergência.",
    steps: [
      { n: "01", title: "Preparar os componentes", d: "Peças conhecidas são organizadas antes da montagem." },
      { n: "02", title: "Recolher e mover",        d: "O robot manipula um componente aprovado." },
      { n: "03", title: "Posicionar",              d: "O componente é posicionado dentro do armário de bateria." },
      { n: "04", title: "Verificar",               d: "O sistema verifica posição e número de série." },
      { n: "05", title: "Aprovação humana",        d: "Um técnico qualificado revê e aprova o resultado." },
    ],
    humanTitle: "O controlo humano continua essencial",
    humanBody:  "O robot apoia trabalho físico repetitivo. Ligações eléctricas, decisões sobre erros, intervenção de segurança e aprovação final do produto permanecem responsabilidades humanas.",
    statusTitle: "Estado do Projecto",
    statuses: [
      { k: "Actual",          v: "Conceito conduzido pelo Fundador com um caso de uso proposto." },
      { k: "Em avaliação",    v: "Configuração do robot, requisitos de segurança, processo de formação e viabilidade do piloto." },
      { k: "Futuro",          v: "Célula piloto, equipa de implementação, software dashboard e implementação industrial." },
    ],
    disclaimer: "Apenas simulação de conceito. A configuração do robot, os requisitos de segurança e o percurso de implementação ainda não foram finalizados. Não é implicada qualquer parceria tecnológica confirmada, aprovação técnica ou implementação comercial.",
    cta: "Discutir um Piloto Futuro",
  },
};

const useR = () => {
  const { lang } = React.useContext(LangCtx);
  return D[lang] || D.en;
};

const scrollToContact = (e) => {
  e.preventDefault();
  const el = document.getElementById("contact") || document.getElementById("cta")
          || document.querySelector("footer") || document.querySelector('[data-testid="footer"]');
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function RobotAssistedAssembly() {
  const t = useR();
  return (
    <section id="robot-assisted-assembly" data-testid="robot-assisted-assembly"
      className="relative border-t" style={{ background: P.bg, borderColor: P.border }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 15% 10%, ${P.cyan}0f 0%, transparent 55%), radial-gradient(circle at 85% 90%, ${P.green}0f 0%, transparent 55%)`,
      }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-semibold px-3 py-1.5 rounded-md"
            style={{ background: `${P.cyan}18`, border: `1px solid ${P.cyan}44`, color: P.cyan }}
            data-testid="ra-status-label">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.cyan }} />
            {t.status}
          </div>
          <h2 className="mt-5 font-semibold tracking-tight text-[26px] sm:text-[34px] lg:text-[44px] leading-[1.05]"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.title}
          </h2>
          <p className="mt-5 max-w-3xl text-[14px] leading-relaxed" style={{ color: P.dim }}>
            {t.intro}
          </p>
        </motion.div>

        {/* Central visual + numbered callouts */}
        <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-10">
          {/* Image — already carries the burned-in "CONCEPT SIMULATION" label
              and the "Robot provides strength / Human provides knowledge"
              strategic tagline, so we let it speak for itself with a soft
              cyan glow. */}
          <motion.figure
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
            className="relative rounded-xl overflow-hidden"
            style={{
              background: P.bg2,
              border: `1px solid ${P.border}`,
              boxShadow: `0 0 0 1px ${P.cyan}22, 0 20px 60px -20px ${P.cyan}55`,
            }}
          >
            <img
              src="/innovation/robot-assembly-concept.webp"
              alt={t.imgAlt}
              loading="lazy"
              width="1600" height="901"
              className="w-full h-auto object-cover"
              data-testid="ra-image"
            />
          </motion.figure>

          {/* Callouts — appear on the right on desktop, below on mobile */}
          <ol className="space-y-3" data-testid="ra-callouts">
            {t.steps.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                data-testid={`ra-step-${s.n}`}
                className="flex gap-3 items-start rounded-lg p-3.5"
                style={{ background: P.surface, border: `1px solid ${P.border}` }}
              >
                <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-mono text-[12px] font-semibold"
                  style={{ background: `${P.cyan}18`, border: `1px solid ${P.cyan}55`, color: P.cyan }}>
                  {s.n}
                </div>
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold" style={{ color: "#fff" }}>{s.title}</div>
                  <div className="mt-0.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{s.d}</div>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>

        {/* Human control message */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          data-testid="ra-human-control"
          className="mt-10 rounded-xl p-5 lg:p-6 flex gap-4 items-start"
          style={{ background: `linear-gradient(135deg, ${P.green}0d 0%, ${P.bg2} 100%)`, border: `1px solid ${P.green}44` }}
        >
          <div className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center"
            style={{ background: `${P.green}22`, border: `1px solid ${P.green}55` }}>
            <ShieldCheck size={18} style={{ color: P.green }} />
          </div>
          <div>
            <div className="text-[14px] font-semibold" style={{ color: "#fff" }}>{t.humanTitle}</div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed max-w-3xl" style={{ color: P.ink, opacity: 0.9 }}>
              {t.humanBody}
            </p>
          </div>
        </motion.div>

        {/* Project status columns */}
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>{t.statusTitle}</div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {t.statuses.map((s, i) => {
              const color = [P.cyan, P.amber, P.green][i];
              return (
                <div key={i} data-testid={`ra-status-${i}`}
                  className="rounded-lg p-5"
                  style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${color}66` }}>
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color }}>{s.k}</div>
                  <div className="mt-2 text-[12.5px] leading-relaxed" style={{ color: P.ink, opacity: 0.9 }}>{s.v}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Concept Presentation Viewer — between status panel and disclaimer */}
        <ConceptPresentationViewer />

        {/* Disclaimer */}
        <div data-testid="ra-disclaimer"
          className="mt-8 text-[11.5px] italic leading-relaxed p-4 rounded-md max-w-4xl"
          style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }}>
          {t.disclaimer}
        </div>

        {/* CTA */}
        <div className="mt-8">
          <a
            href="#contact"
            onClick={scrollToContact}
            data-testid="ra-cta"
            className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
            style={{ background: P.cyan, color: "#04121a" }}
          >
            {t.cta} <ChevronRight size={13} />
          </a>
        </div>
      </div>
    </section>
  );
}
